<template>
  <PosterEditor
    v-if="editorVisible"
    :id="editId"
    @success="onEditorSuccess"
    @cancel="closeEditor"
  />
  <ContentWrap v-else>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      show-icon
      title="分销邀请海报"
      description="配置个人邀请下线海报模版（背景 + 头像/昵称/小程序码）。启用后，C 端分销中心「邀请海报」按模版合成。坐标以预览画布宽 320 为准。"
    />

    <el-form class="-mb-15px" :inline="true" :model="queryParams" label-width="68px">
      <el-form-item label="名称">
        <el-input
          v-model="queryParams.title"
          clearable
          class="!w-220px"
          placeholder="海报名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button
          v-hasPermi="['distribution:brokerage-poster:create']"
          type="primary"
          @click="openCreate"
        >
          <Icon icon="ep:plus" class="mr-5px" />新建海报
        </el-button>
      </el-form-item>
    </el-form>

    <div class="section-title">已使用海报</div>
    <div v-loading="loading" class="poster-list">
      <div v-if="!enabledList.length" class="poster-empty">暂无已使用海报</div>
      <div v-for="item in enabledList" :key="item.id" class="poster-card">
        <span class="poster-card__badge">已使用</span>
        <div class="poster-card__thumb">
          <el-image :src="item.thumb || item.bgImg" fit="cover" class="poster-card__img">
            <template #error>
              <div class="poster-card__img-fallback">无图</div>
            </template>
          </el-image>
        </div>
        <div class="poster-card__title">{{ item.title }}</div>
        <div class="poster-card__ops">
          <el-button
            v-hasPermi="['distribution:brokerage-poster:update']"
            link
            type="primary"
            @click="openEdit(item.id!)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['distribution:brokerage-poster:update-status']"
            link
            type="warning"
            @click="toggleStatus(item, 0)"
          >
            取消使用
          </el-button>
        </div>
      </div>
    </div>

    <div class="section-title">海报仓库</div>
    <div v-loading="loading" class="poster-list">
      <div v-if="!warehouseList.length" class="poster-empty">暂无仓库海报</div>
      <div v-for="item in warehouseList" :key="item.id" class="poster-card">
        <div class="poster-card__thumb">
          <el-image :src="item.thumb || item.bgImg" fit="cover" class="poster-card__img">
            <template #error>
              <div class="poster-card__img-fallback">无图</div>
            </template>
          </el-image>
        </div>
        <div class="poster-card__title">{{ item.title }}</div>
        <div class="poster-card__ops">
          <el-button
            v-hasPermi="['distribution:brokerage-poster:update']"
            link
            type="primary"
            @click="openEdit(item.id!)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['distribution:brokerage-poster:update-status']"
            link
            type="success"
            @click="toggleStatus(item, 1)"
          >
            立即使用
          </el-button>
          <el-button
            v-hasPermi="['distribution:brokerage-poster:delete']"
            link
            type="danger"
            @click="handleDelete(item)"
          >
            删除
          </el-button>
        </div>
      </div>
    </div>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import * as PosterApi from '@/api/mall/trade/brokerage/poster'
import type { BrokeragePosterVO } from '@/api/mall/trade/brokerage/poster'
import PosterEditor from './PosterEditor.vue'

defineOptions({ name: 'TradeBrokeragePoster' })

const message = useMessage()
const loading = ref(false)
const editorVisible = ref(false)
const editId = ref<number>()
const allList = ref<BrokeragePosterVO[]>([])
const enabledList = ref<BrokeragePosterVO[]>([])
const warehouseList = ref<BrokeragePosterVO[]>([])
const queryParams = reactive({
  title: '',
  pageNo: 1,
  pageSize: 100
})

async function getList() {
  loading.value = true
  try {
    const data = await PosterApi.getBrokeragePosterPage(queryParams)
    allList.value = data.list || []
    enabledList.value = allList.value.filter((i) => i.status === 1)
    warehouseList.value = allList.value.filter((i) => i.status !== 1)
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  getList()
}

function openCreate() {
  editId.value = undefined
  editorVisible.value = true
}

function openEdit(id: number) {
  editId.value = id
  editorVisible.value = true
}

function closeEditor() {
  editorVisible.value = false
  editId.value = undefined
}

function onEditorSuccess() {
  closeEditor()
  getList()
}

async function toggleStatus(item: BrokeragePosterVO, status: number) {
  const action = status === 1 ? '立即使用' : '取消使用'
  try {
    await message.confirm(`确认${action}海报「${item.title}」吗？`)
    await PosterApi.updateBrokeragePosterStatus(item.id!, status)
    message.success('操作成功')
    await getList()
  } catch {
    /* cancel */
  }
}

async function handleDelete(item: BrokeragePosterVO) {
  try {
    await message.delConfirm(`确认删除海报「${item.title}」吗？`)
    await PosterApi.deleteBrokeragePoster(item.id!)
    message.success('删除成功')
    await getList()
  } catch {
    /* cancel */
  }
}

onMounted(() => {
  getList()
})
</script>

<style scoped lang="scss">
.section-title {
  margin: 20px 0 12px;
  padding: 10px 16px;
  background: #f6f7f9;
  font-size: 15px;
  color: #333;
  font-weight: 500;
}

.poster-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  min-height: 40px;
}

.poster-empty {
  width: 100%;
  padding: 24px;
  text-align: center;
  color: #999;
  font-size: 14px;
  border: 1px solid #efefef;
  border-radius: 4px;
}

.poster-card {
  position: relative;
  width: 182px;
  border: 1px solid #efefef;
  border-radius: 2px;
  overflow: hidden;
  background: #fff;

  &__badge {
    position: absolute;
    z-index: 1;
    left: -26px;
    top: -6px;
    width: 80px;
    height: 40px;
    line-height: 55px;
    text-align: center;
    font-size: 12px;
    color: #fff;
    background: #00aeff;
    transform: rotate(-45deg);
  }

  &__thumb {
    width: 180px;
    height: 180px;
    background: #f9f9f9;
  }

  &__img {
    width: 180px;
    height: 180px;
  }

  &__img-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 180px;
    height: 180px;
    color: #bbb;
    font-size: 13px;
  }

  &__title {
    height: 36px;
    padding: 0 8px;
    line-height: 36px;
    font-size: 14px;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__ops {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 8px 10px;
    border-top: 1px solid #efefef;
  }
}
</style>
